import { useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useScroll, useSpring } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon, MessageCircleIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { MentionedProducts } from '../journal/MentionedProducts';
import { ProductImage } from '../../ui/ProductImage';
import { articlePath } from '../journal/journalRules';
import { useCatalog } from '../../contexts/CatalogContext';
import { salon } from '../../data/content';
import { articleImage } from '../../data/placeholderImages';
import { usePages } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { useLayerY } from '../../hooks/useParallax';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { btnPrimaryLg } from '../../ui/styles';
import { HOME_ARTICLES, JOURNAL_PRODUCTS } from './homeRules';

/** Hoe snel het zwevende beeld de muis volgt. */
const FOLLOW = {
  stiffness: 380,
  damping: 32,
  mass: 0.6
};

/**
 * "Wat ik in de salon het vaakst uitleg", op klei: het enige blok dat Sarahs expertise
 * laat zien krijgt een eigen, donker vlak (shell-tekst, ≥ 5,6:1; nooit muted of kleine blush op klei).
 *
 * - Links het nieuwste artikel groot; daaronder "Genoemd in dit artikel": de producten op een
 *   licht vlak, met een plusje. Zo verkoopt het journal ook, zonder te doen alsof het een vragenlijst is.
 * - Rechts de andere artikelen als rijen. Wijs je er met de muis een aan, dan zweeft het beeld mee
 *   (alleen computer met muis, niet bij "minder beweging").
 * - Onderaan de app-kaart in blush: het lichtste vlak, dus het slot van het blok.
 */
export function HomeJournal() {
  const pages = usePages();
  const {
    articles,
    productsByIds
  } = useCatalog();
  const reduce = useReducedMotion();
  const copy = pages.home.journal;
  const [lead, ...rest] = articles.slice(0, HOME_ARTICLES);
  const [hovered, setHovered] = useState<string | null>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, FOLLOW);
  const y = useSpring(my, FOLLOW);
  // De grote foto beweegt binnen zijn kader licht mee.
  const leadRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: leadRef,
    offset: ['start end', 'end start']
  });
  const leadY = useLayerY(scrollYProgress, [-28, 28]);
  if (!lead) return null;
  const mentioned = productsByIds(lead.productIds ?? []).slice(0, JOURNAL_PRODUCTS);
  // Elk artikel heeft een beeld (eigen, of het tijdelijke per artikel), dus elk artikel krijgt een voorbeeld.
  const preview = rest.find(a => a.slug === hovered);
  const previewImage = preview ? articleImage(preview) : null;
  const onMove = (e: MouseEvent<HTMLUListElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    mx.set(e.clientX - box.left);
    my.set(e.clientY - box.top);
  };
  return <Section tone="clay" labelledBy="home-journal" contained={false} data-id="element-7444" data-mp="1na1dechql5gx">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7445" data-mp="199i5d5d9tzer">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-7446" data-mp="1e7s4fr249k5n">
          <div className="max-w-2xl" data-id="element-7447" data-mp="1e81utfr2ptbv">
            <h2 id="home-journal" className={sectionHeading} data-id="element-7448" data-mp="21xmha8mfr8pw">
              <HeadingText heading={copy.heading} accentClassName="text-blush" data-id="element-7449" data-mp="21y5vpfcvnez6" />
            </h2>
            {copy.body && <p className="mt-5 max-w-xl text-[17px] leading-relaxed" data-id="element-7450" data-mp="26ya5kmja5bgf">{fill(copy.body)}</p>}
          </div>
          {copy.cta && <Link to="/journal" className={`${quietLink} text-shell`} data-id="element-7451" data-mp="1x64ctal7eq06">
              {fill(copy.cta)}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7452" data-mp="2kvqgaa8lzrdz" />
            </Link>}
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-7453" data-mp="2pojiaqcp0lmd">
          <article className="lg:col-span-7" data-id="element-7454" data-mp="111e1z44p77su">
            <Link to={articlePath(lead)} className="group block" data-id="element-7455" data-mp="10z1b21gypn3n">
              <div ref={leadRef} className="relative aspect-[4/3] overflow-hidden bg-ink/20" data-id="element-7456" data-mp="wyr70ucqd6rf">
                <motion.div style={leadY ? {
                y: leadY
              } : undefined} className="absolute inset-x-0 -inset-y-[5%]" data-id="element-7457" data-mp="s1im4m076omh">
                  <MediaView media={{
                  image: articleImage(lead),
                  alt: ''
                }} sizes="(min-width: 1024px) 55vw, 100vw" className="h-full w-full transition-transform duration-300 ease-soft group-hover:scale-[1.03]" data-id="element-7458" data-mp="n2xl4kd4g0wz" />
                </motion.div>
              </div>
              <p className="mt-6 text-[13px]" data-id="element-7459" data-mp="1608ks62o0wcy">
                {lead.category} · {lead.readTime}
              </p>
              <h3 className="mt-2 type-sub underline decoration-transparent underline-offset-[8px] transition-[text-decoration-color] duration-150 ease-soft group-hover:decoration-shell/60" data-id="element-7460" data-mp="23fcqw6tke9mw">
                {lead.title}
              </h3>
              <p className="mt-3 max-w-xl text-[17px] leading-relaxed" data-id="element-7461" data-mp="261c8wev4fmlh">{lead.excerpt}</p>
            </Link>

            {mentioned.length > 0 && <motion.div initial={reduce ? false : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '0px 0px -10% 0px'
          }} transition={{
            duration: 0.25,
            ease
          }} className="mt-8 bg-shell p-5 text-ink sm:p-6" data-id="element-7462" data-mp="2fy8dwalt9qla">
                <h4 className="text-[13px] text-muted" data-id="element-7463" data-mp="2twor8ke6ifce">Genoemd in dit artikel</h4>
                <MentionedProducts products={mentioned} data-id="element-7464" data-mp="2yog9m7n6lerg" />
              </motion.div>}
          </article>

          {rest.length > 0 && <ul onMouseMove={reduce ? undefined : onMove} onMouseLeave={() => setHovered(null)} className="relative divide-y divide-shell/25 border-y border-shell/25 lg:col-span-5 lg:self-start" data-id="element-7465" data-mp="33pvxs8kno1e2">
              {rest.map(a => <li key={a.slug} data-id="element-7466" data-mp="1djzc13ahxkih">
                  <Link to={articlePath(a)} onMouseEnter={() => setHovered(a.slug)} className="group grid grid-cols-[1fr_auto] gap-4 py-7" data-id="element-7467" data-mp="18kwh4bo8cuo8">
                    <span data-id="element-7468" data-mp="1mcujm3nfrsbf">
                      <span className="block text-[13px]" data-id="element-7469" data-mp="1me5pqa4ctc5p">
                        {a.category} · {a.readTime}
                      </span>
                      <span className="mt-2 block type-title transition-transform duration-200 ease-soft group-hover:translate-x-1" data-id="element-7470" data-mp="1w9pg6h05fin2">
                        {a.title}
                      </span>
                      <span className="mt-2 block text-[15px] leading-relaxed" data-id="element-7471" data-mp="25bf9jsdlcrnh">{a.excerpt}</span>
                    </span>
                    <ArrowUpRightIcon className="mt-1 h-5 w-5 transition-transform duration-200 ease-soft group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7472" data-mp="21ad7z4w7c3ic" />
                  </Link>
                </li>)}

              {!reduce && <AnimatePresence data-id="element-7473" data-mp="th1uzsobriu">
                  {preview && previewImage && <motion.div key={preview.slug} aria-hidden="true" style={{
              x,
              y
            }} initial={{
              opacity: 0,
              scale: 0.96,
              rotate: -2
            }} animate={{
              opacity: 1,
              scale: 1,
              rotate: -4
            }} exit={{
              opacity: 0,
              scale: 0.96
            }} transition={{
              duration: 0.18,
              ease
            }} className="pointer-events-none absolute left-0 top-0 z-raised hidden lg:block" data-id="element-7474" data-mp="3j76ry20anpvw">
                      <div className="-translate-x-[115%] -translate-y-1/2 bg-shell p-2 shadow-[0_18px_40px_-16px_rgba(43,36,32,0.45)]" data-id="element-7475" data-mp="3587775fwjv50">
                        <ProductImage src={previewImage} alt="" fit="cover" width={176} className="h-[13rem] w-44" data-id="element-7476" data-mp="yemy4czcjbh6" />
                      </div>
                    </motion.div>}
                </AnimatePresence>}
            </ul>}
        </div>

        {/* De app-kaart: het lichtste vlak, dus het slot van het blok. */}
        {copy.askLine && <div className="mt-14 flex flex-col gap-5 bg-blush p-6 text-ink sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:p-8 lg:mt-16" data-id="element-7477" data-mp="228lo7iuhtnz4">
            <div className="flex items-start gap-4" data-id="element-7478" data-mp="1ta9uv928sc2z">
              <MessageCircleIcon className="mt-1.5 h-5 w-5 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7479" data-mp="1t7njszg57eg0" />
              <div data-id="element-7480" data-mp="1jbkl63ki1xxt">
                <p className="type-title" data-id="element-7481" data-mp="1jb12ulqxt7j9">{fill(copy.askLine)}</p>
                {copy.askBody && <p className="mt-1 text-[15px] leading-relaxed text-muted" data-id="element-7482" data-mp="1f9z2u2p74rg8">{fill(copy.askBody)}</p>}
              </div>
            </div>
            {copy.askCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${btnPrimaryLg} shrink-0`} data-id="element-7483" data-mp="3doshy0s1cjm3">
                {fill(copy.askCta)}
                <span className="sr-only" data-id="element-7484" data-mp="10fbyral49rup">{shopLabels.viaWhatsApp}</span>
              </a>}
          </div>}
      </div>
    </Section>;
}